/// <reference types="vite/client" />

import axios, {
  isCancel,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { tokenHandler } from '../utils/tokenHandler';
import { ApiError, ApiErrorCode } from './apiError';

interface ImportMetaEnv {
  readonly VITE_API_BASE_URL: string;
  readonly VITE_API_TIMEOUT: string;
}

interface ImportMeta {
  readonly env: ImportMetaEnv;
}

interface RequestMetadata {
  startTime: number;
}

type ApiRequestConfig = InternalAxiosRequestConfig & {
  metadata?: RequestMetadata;
};

const createApiInstance = (): AxiosInstance =>
  axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    timeout: Number(import.meta.env.VITE_API_TIMEOUT) || 60000,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });

const api = createApiInstance();
let unauthorizedRedirectInFlight = false;

const redirectToLogin = () => {
  if (window.location.pathname === '/' && window.location.search === '' && window.location.hash === '') {
    return;
  }

  window.history.replaceState(null, '', '/');
  window.dispatchEvent(new PopStateEvent('popstate'));
};

const handleUnauthorizedSession = async () => {
  if (unauthorizedRedirectInFlight) {
    return;
  }

  unauthorizedRedirectInFlight = true;

  const [{ store }, authModule, { returnPath }] = await Promise.all([
    import('../app/store'),
    import('../features/auth/authSlice'),
    import('../features/auth/returnPath'),
  ]);

  // Before the session is cleared: the next login returns to this screen.
  returnPath.save();

  tokenHandler.handleExpired(() => {
    void store.dispatch(authModule.logout());
    redirectToLogin();
  });

  window.setTimeout(() => {
    unauthorizedRedirectInFlight = false;
  }, 1000);
};

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const nextConfig = config as ApiRequestConfig;
    const token = tokenHandler.get();

    if (token) {
      nextConfig.headers.Authorization = `Bearer ${token}`;
    }

    nextConfig.metadata = { startTime: Date.now() };
    nextConfig.url = String(nextConfig.url ?? '').replace(/^\/+/, '/');

    return nextConfig;
  },
  (error) => Promise.reject(ApiError.fromAxiosError(error))
);

api.interceptors.response.use(
  (response: AxiosResponse) => {
    if (import.meta.env.DEV) {
      const config = response.config as ApiRequestConfig;
      const duration = Date.now() - (config.metadata?.startTime ?? Date.now());
      console.debug(
        `[API] ${response.config.method?.toUpperCase()} ${response.config.url} - ${duration}ms`
      );
    }

    return response;
  },
  async (error) => {
    if (isCancel(error)) {
      return Promise.reject(ApiError.fromCancel());
    }

    const apiError = ApiError.fromAxiosError(error);

    if (import.meta.env.DEV) {
      console.error(
        `[API Error] ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
        apiError
      );
    }

    const isLoginRequest = error.config?.url?.includes('/auth/login');

    // A 401 earned by a token this browser no longer holds (another tab
    // refreshed or signed in again while the call was on the wire) says
    // nothing about the session now in storage, so it must not end it.
    const sentAuthorization = String(error.config?.headers?.Authorization ?? '');
    const currentToken = tokenHandler.get();
    const sentWithStaleToken =
      sentAuthorization.startsWith('Bearer ') &&
      currentToken !== null &&
      sentAuthorization !== `Bearer ${currentToken}`;

    if (
      (apiError.status === 401 || (apiError.status === 403 && !tokenHandler.isValid())) &&
      !isLoginRequest &&
      !sentWithStaleToken &&
      apiError.code !== ApiErrorCode.CANCELLED
    ) {
      setTimeout(() => {
        void handleUnauthorizedSession();
      }, 0);
    }

    return Promise.reject(apiError);
  }
);

export const createCancellableRequest = <T = unknown>(
  url: string,
  config: AxiosRequestConfig = {}
) => {
  const controller = new AbortController();
  return {
    request: controller,
    promise: api.get<T>(url, { ...config, signal: controller.signal }),
  };
};

export const cancelAllRequests = () => {
  // Axios does not expose a global request registry.
};

export default api;
